import {
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { getBlueticksVersion, logoutWrapperThunk } from "@common-reducers/UserThunks";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { MaybeUser } from "@common/types/interfaces";
import { accessFormPopupActions } from "@common/reducers/AccessFormPopupReducer";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { selectUserObject } from "@common-reducers/UserSelectors";
import { cn } from "@/lib/utils";
import { LogIn, LogOut } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';

type Props = {
    handleClose: () => void;
}

export default function ManageExtensionMenu({
    handleClose,
}: Props) {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch()
    const user: MaybeUser = useSelector(selectUserObject);
    const userIsLoggedIn = !!user?._id;
    const accessToken = useAppSelector((state) => state.UserReducer.accessToken);
    const currentExtensionVersion = useAppSelector((state) => state.WhatsAppReducer.currentExtensionVersion);

    useEffect(() => {
        const fetchVersion = async () => {
         await dispatch(getBlueticksVersion());
        };
        fetchVersion();
    }, []);

    const openSignup = () => {
        dispatch(accessFormPopupActions.setStatus("Signup"));
        handleClose();
    };

    const openLogin = () => {
        dispatch(accessFormPopupActions.setStatus("Login"));
        handleClose();
    };

    return (
        <div className="flex flex-col py-1">
            {userIsLoggedIn ? (
                <DropdownMenuItem 
                    className="cursor-pointer"
                    onClick={(e) => {
                        e.preventDefault();
                        dispatch(logoutWrapperThunk({ accessToken: accessToken ?? null }));
                        handleClose();
                    }}
                >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>{t('manageExtensionMenu.logout', 'Logout')}</span>
                </DropdownMenuItem>
            ) : (
                <DropdownMenuItem 
                    className="cursor-pointer"
                    onClick={(e) => {
                        e.preventDefault();
                        openLogin();
                    }}
                >
                    <LogIn className="mr-2 h-4 w-4" />
                    <span>{t('manageExtensionMenu.login', 'Login')}</span>
                </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />

            {user?.displayEmail && (
                <DropdownMenuLabel className="font-normal text-muted-foreground truncate max-w-[240px]">
                    {user.displayEmail}
                </DropdownMenuLabel>
            )}

            <DropdownMenuLabel className="font-normal text-muted-foreground text-xs">
                {t('manageExtensionMenu.version', 'version')} {currentExtensionVersion}
            </DropdownMenuLabel>

            {user?.isGuest && (
                <div className="p-4 bg-muted/50 mt-2 text-sm">
                    <p className="mb-2">
                        {t('manageExtensionMenu.guestMessage', 'You are currently logged in as <bold>a guest.</bold>')
                            .split('<bold>').map((part, i) => 
                                i === 0 ? part : <span key={i} className="font-semibold">{part.replace('</bold>', '')}</span>
                            )}
                    </p>
                    <p className="mb-2">
                        <span 
                            className="text-primary cursor-pointer hover:underline" 
                            onClick={openSignup}
                        >
                            {t('manageExtensionMenu.signUp', 'Sign up')}
                        </span> {t('manageExtensionMenu.signUpPrompt', '<link>Sign up</link> to prevent losing your data.')
                            .replace('<link>Sign up</link>', '')
                            .replace('<link>', '')
                            .replace('</link>', '')
                            .trim()}
                    </p>
                    <p className="mb-4">
                        <span 
                            className="text-primary cursor-pointer hover:underline" 
                            onClick={openLogin}
                        >
                            {t('manageExtensionMenu.login', 'Login')}
                        </span> {t('manageExtensionMenu.loginPrompt', '<link>Login</link> to switch to an existing account.')
                            .replace('<link>Login</link>', '')
                            .replace('<link>', '')
                            .replace('</link>', '')
                            .trim()}
                    </p>
                    <div className="flex gap-2">
                        <button
                            className="px-3 py-1 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90"
                            onClick={openSignup}
                        >
                            {t('manageExtensionMenu.signUp', 'Sign up')}
                        </button>
                        <button
                            className="px-3 py-1 border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md text-sm"
                            onClick={openLogin}
                        >
                            {t('manageExtensionMenu.login', 'Login')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}
